import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, CartUpdateEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { cartPerformance } from '@theme/performance';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { DeliveryCartAttributes } from '@theme/delivery-cart-attributes';
import { openPostalCodeDrawer } from '@theme/delivery-core';
import { storePendingProductData } from '@theme/meal-delivery-pending-product';
import { getSurveySellingPlanId } from '@theme/thuysvers-cart-selling-plan-utils';

// Constants
const CONSOLE_PREFIX = '[PRODUCT-FORM]';

// Helper Functions
/**
 * @param {string} level - Log level (log, warn, error)
 * @param {string} message - Message to log
 * @param {...any} args - Additional arguments
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`🔍 ${CONSOLE_PREFIX} ${message}`, ...args);
}

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

const ACTIVE_STEP_KEY = 'tv_subscription_active_step_handle';

/**
 * Get _profile_key from cart item properties (matches collection-subscription-footer logic).
 * @param {any} item - Cart line item from /cart.js
 * @returns {string}
 */
function getItemProfileKey(item) {
  const p = item?.properties;
  if (!p) return '';
  if (typeof p === 'object' && !Array.isArray(p) && p._profile_key != null) return String(p._profile_key).trim();
  if (Array.isArray(p)) {
    const e = p.find((x) => x && (x.name === '_profile_key' || x.key === '_profile_key'));
    return e ? String(e.value || '').trim() : '';
  }
  return '';
}

/**
 * Check if we are on the subscription collection template.
 * Matches Liquid logic: template contains 'subscription' or 'abonnement'.
 * @returns {boolean}
 */
function isSubscriptionCollectionTemplate() {
  const t = typeof Theme !== 'undefined' ? Theme?.template?.name : '';
  const name = String(t || '').toLowerCase();
  return name.includes('subscription') || name.includes('abonnement');
}

/**
 * Shopify merges line items when variant, line properties, and selling plan match.
 * A unique hidden property per unit keeps each add as its own line (cart + checkout).
 * Only used on the subscription collection template in subscription mode (see handleSubmit).
 * @returns {string}
 */
function newSubscriptionLineUid() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * @param {FormData} fd
 * @returns {Record<string, string>}
 */
function collectCartLinePropertiesFromFormData(fd) {
  /** @type {Record<string, string>} */
  const props = {};
  for (const [key, value] of fd.entries()) {
    if (typeof value !== 'string') continue;
    const m = /^properties\[(.+)]$/.exec(key);
    const propKey = m?.[1];
    if (propKey) props[propKey] = value;
  }
  return props;
}

/** @returns {string} */
function getActiveStepHandle() {
  try {
    const raw = sessionStorage.getItem(ACTIVE_STEP_KEY);
    return String(raw || '').trim() || 'p1';
  } catch {
    return 'p1';
  }
}

/** @param {string} h */
function isPersonStepHandle(h) {
  return /^p\d+$/i.test(String(h || ''));
}

/**
 * Build variant quantity map from cart items, filtered by profile when toggler active.
 * @param {any[]} items
 * @param {{ key: string | undefined, mode: 'ecomm' | 'subscription' | undefined } | undefined} profileContext
 * @returns {Map<number, number>}
 */
function buildVariantQuantityMap(items, profileContext) {
  const variantQuantities = new Map();
  const profileKey = profileContext?.key;

  items.forEach((/** @type {any} */ item) => {
    if (profileKey) {
      const itemProfile = getItemProfileKey(item);
      const matchesStep = itemProfile === profileKey;
      const matchesAddon = isPersonStepHandle(profileKey) && (itemProfile === 'soup' || itemProfile === 'dessert');
      if (!matchesStep && !matchesAddon) return;
    }
    const vid = item.variant_id;
    variantQuantities.set(vid, (variantQuantities.get(vid) || 0) + (item.quantity || 0));
  });
  return variantQuantities;
}

/**
 * Sync the custom quick add modal footer: variant badges and +/- button state from cart.
 * Call after morph or when cart updates while modal is open.
 * @param {{ items?: any[] } | null} [cartOverride] - Use this cart; if omitted, fetches from API
 */
export async function syncQuickAddModalFooterState(cartOverride) {
  const modalFooter = document.getElementById('custom-quick-add-modal__footer');
  if (!(modalFooter instanceof HTMLElement)) return;

  try {
    const cartData = cartOverride ?? await DeliveryCartAttributes.getCartData();
    const items = Array.isArray(/** @type {any} */ (cartData)?.items) ? /** @type {any} */ (cartData).items : [];
    const profileContext = (() => {
      const toggle = document.querySelector('[data-ecomm-person-toggle]:not(.no-disp)');
      const footer = document.querySelector('custom-collection-subscription-footer');
      /** @param {Element | null} el */
      const visible = (el) => el && (/** @type {HTMLElement} */ (el).offsetHeight > 0 || /** @type {HTMLElement} */ (el).offsetParent !== null);
      if (visible(toggle)) {
        const toggleRoot = toggle?.closest('[data-ecomm-person-toggle]');
        const mode = (toggleRoot instanceof HTMLElement ? String(toggleRoot.dataset?.ecommToggleMode || '').trim().toLowerCase() : '') || 'ecomm';

        if (mode === 'ecomm') {
          const checked = toggle.querySelector('input[name="ecomm-profile"]:checked');
          const key = checked instanceof HTMLInputElement ? String(checked.value || '').trim() : getActiveStepHandle();
          return { key, mode: 'ecomm' };
        }

        return { key: getActiveStepHandle(), mode: 'subscription' };
      }
      if (visible(footer)) return { key: getActiveStepHandle(), mode: 'subscription' };
      return undefined;
    })();
    const variantQuantities = buildVariantQuantityMap(items, profileContext);

    // Variant badges in dropdown
    const badges = modalFooter.querySelectorAll('[data-variant-qty-badge], .cart-stock-indicator[data-variant-id]');
    badges.forEach((badge) => {
      if (!(badge instanceof HTMLElement)) return;
      const vid = parseInt(badge.getAttribute('data-variant-id') || '', 10);
      if (isNaN(vid)) return;
      const qty = variantQuantities.get(vid) || 0;
      badge.textContent = String(qty);
      badge.classList.toggle('no-disp', qty === 0);
    });

    // Minus button: show when selected variant has qty > 0
    const form = modalFooter.querySelector('form');
    const variantInput = form?.querySelector('input[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement ? parseInt(String(variantInput.value || ''), 10) : NaN;
    const productQty = !isNaN(variantId) ? (variantQuantities.get(variantId) || 0) : 0;
    const minusBtn = modalFooter.querySelector('[data-product-minus-btn], .product-card-addtocart-btn--minus');
    if (minusBtn instanceof HTMLElement) {
      minusBtn.classList.toggle('no-disp', productQty === 0);
    }
  } catch (e) {
    logMessage('warn', 'syncQuickAddModalFooterState:', e);
  }
}

/**
 * Shared function to handle product form submission logic
 * @param {HTMLFormElement} form - The form element
 * @param {Event} event - The event object
 */
async function handleProductSubmission(form, event) {
  logMessage('log', 'Product submission triggered');

  // Get form data first
  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const productIdInput = form.querySelector('input[name="product-id"]');

  if (!variantInput) {
    logMessage('error', 'No variant input found');
    return false;
  }

  const variantId = /** @type {HTMLInputElement} */ (variantInput).value;
  const quantity = quantityInput ? /** @type {HTMLInputElement} */ (quantityInput).value : '1';
  const productId = productIdInput ? /** @type {HTMLInputElement} */ (productIdInput).value : '';


  // Check postal code and delivery time FIRST
  let hasCompleteData;
  let cartData = null;

  if (/** @type {any} */ (window).deliveryManager) {
    const result = await /** @type {any} */ (window).deliveryManager.hasCompleteDeliveryInfo();
    hasCompleteData = result.hasData;
    cartData = result.cart;
  } else {
    const result = await DeliveryCartAttributes.hasPostalCodeAndDeliveryTime();
    hasCompleteData = result.hasData;
    cartData = result.cart;
  }

  logMessage('log', 'Checking postal code and delivery time:', hasCompleteData);
  logMessage('log', 'Cart data available:', !!cartData);

  if (!hasCompleteData) {
    // Check if postal code form is already filled (from customer metafield)
    const postcodeForm = document.querySelector('#postcode-form');
    const digitsInput = postcodeForm?.querySelector('[name="postcode_digits"]');
    const lettersInput = postcodeForm?.querySelector('[name="postcode_letters"]');

    const hasFilledPostcode = /** @type {HTMLInputElement} */ (digitsInput)?.value && /** @type {HTMLInputElement} */ (lettersInput)?.value;
    logMessage('log', 'Has filled postal code in form:', hasFilledPostcode);

    if (hasFilledPostcode) {
      // Postal code is pre-filled - auto-submit the form
      logMessage('log', 'Postal code is pre-filled, auto-submitting form');

      // Get the product ID from the current product card or product-form-component
      const productCard = form.closest('product-card');
      const productFormComponent = form.closest('product-form-component');
      const productId = productCard?.getAttribute('data-product-id') || productFormComponent?.getAttribute('data-product-id') || '';

      logMessage('log', 'Found variant ID:', variantId);
      storePendingProductData(variantId, quantity, productId);

      // Auto-submit the postal code form
      if (postcodeForm) {
        logMessage('log', 'Auto-submitting postal code form');
        /** @type {HTMLFormElement} */ (postcodeForm).requestSubmit();
      }

      // Prevent default form submission
      if (event) {
        event.preventDefault();
      }
      return false;
    } else {
      // No postal code - open postal code drawer
      logMessage('log', 'No postal code found, opening postal drawer');

      const productCard = form.closest('product-card');
      const productFormComponent = form.closest('product-form-component');
      const productId = productCard?.getAttribute('data-product-id') || productFormComponent?.getAttribute('data-product-id') || '';

      logMessage('log', 'Found variant ID:', variantId);
      storePendingProductData(variantId, quantity, productId);
      openPostalCodeDrawer();

      // Prevent default form submission
      if (event) {
        event.preventDefault();
      }
      return false;
    }
  }

  // const cartData = await /** @type {any} */ (window).DeliveryCartAttributes.getCartData();

  // Check if product is compatible with current delivery day
  if (productId) {
    // Use the cart data we already fetched to avoid redundant API calls
    logMessage('log', 'Checking product compatibility with cart data:', !!cartData);
    const isCompatible = await /** @type {any} */ (window).ProductCompatibility?.isProductCompatibleWithDeliveryDay(productId, cartData);
    if (!isCompatible) {
      // Show incompatibility popup
      await /** @type {any} */ (window).ProductCompatibility?.showIncompatibilityPopup(productId);

      // Prevent form submission
      if (event) {
        event.preventDefault();
      }
      return false;
    }
  }

  return true;
}



/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  /** @type {number | undefined} */
  #minusAnimationTimeout;

  /** @type {number | undefined} */
  #minusCleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    if (this.#minusAnimationTimeout) clearTimeout(this.#minusAnimationTimeout);
    if (this.#minusCleanupTimeout) clearTimeout(this.#minusCleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Shows loading state on the add to cart button.
   */
  showLoading() {
    const button = this.refs.addToCartButton;
    if (button) {
      button.classList.add('atc-loading');
      button.disabled = true;
    }
  }

  /**
   * Hides loading state on the add to cart button.
   */
  hideLoading() {
    const button = this.refs.addToCartButton;
    if (button) {
      button.classList.remove('atc-loading');
      button.disabled = false;
    }
  }

  /**
   * Handles minus button click to decrease quantity in cart.
   * Performs the cart change directly (no dependency on cart-items-component) for reliability.
   * Shows spinner, then checkmark on success (no fly animation).
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  async decreaseQuantity(event) {
    event.preventDefault();
    const minusBtn = this.refs.minusButton;
    const minusButton = minusBtn instanceof HTMLButtonElement ? minusBtn : Array.isArray(minusBtn) ? minusBtn[0] : null;
    if (!(minusButton instanceof HTMLButtonElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return;

    const variantId = String(variantInput.value || '').trim();
    if (!variantId) return;

    /** @param {boolean} success */
    const finishMinus = (success) => {
      minusButton.classList.remove('atc-loading');
      minusButton.disabled = false;
      if (success) {
        this.#animateMinusButton(minusButton);
      }
    };

    try {
      minusButton.classList.add('atc-loading');
      minusButton.disabled = true;

      const cartRes = await fetch(`${Theme.routes.cart_url}.js`);
      if (!cartRes.ok) {
        finishMinus(false);
        return;
      }
      const cart = await cartRes.json();
      const items = cart.items || [];

      const activeStep = getActiveStepHandle();
      let index = items.findIndex((/** @type {any} */ item) => {
        if (String(item.variant_id) !== String(variantId)) return false;
        const itemProfile = getItemProfileKey(item);
        return itemProfile === activeStep;
      });
      if (index < 0) {
        const matches = [];
        for (let i = 0; i < items.length; i++) {
          if (String(items[i].variant_id) === String(variantId)) matches.push(i);
        }
        if (matches.length === 1) index = matches[0];
      }
      if (index < 0) {
        finishMinus(false);
        return;
      }

      const line = index + 1;
      const currentQty = Number(items[index].quantity) || 0;
      const newQuantity = Math.max(0, currentQty - 1);

      const sectionsToUpdate = this.#buildSectionsForCartChange();
      const body = JSON.stringify({
        line,
        quantity: newQuantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) {
        finishMinus(false);
        return;
      }

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        finishMinus(false);
        return;
      }

      if (parsed.errors) {
        finishMinus(false);
        return;
      }

      let itemCount = 0;
      const cartSectionId = document.querySelector('cart-items-component')?.getAttribute('data-section-id');
      if (cartSectionId && parsed.sections?.[cartSectionId]) {
        const sectionHtml = new DOMParser().parseFromString(parsed.sections[cartSectionId], 'text/html');
        const countEl = sectionHtml.querySelector('[ref="cartItemCount"]');
        if (countEl?.textContent) itemCount = parseInt(countEl.textContent, 10) || 0;
      }
      if (itemCount === 0 && Array.isArray(parsed.items)) {
        itemCount = parsed.items.reduce((/** @type {number} */ sum, /** @type {{ quantity?: number }} */ it) => sum + (Number(it.quantity) || 0), 0);
      }

      const productCardLink = this.closest('product-card-link');
      const productId = productCardLink?.getAttribute('data-product-id') || items[index]?.product_id;
      const quickAddDialog = document.getElementById('custom-quick-add-dialog');
      const fromQuickAddModal = !!(quickAddDialog && quickAddDialog.contains(this));

      document.dispatchEvent(
        new CartUpdateEvent({}, 'add-to-cart-component', /** @type {any} */ ({
          itemCount,
          source: 'add-to-cart-component',
          sections: parsed.sections,
          productId: productId ?? undefined,
          fromQuickAddModal,
          cartData: parsed.items ? { items: parsed.items } : undefined,
        }))
      );

      if (fromQuickAddModal) {
        const footer = document.querySelector('custom-collection-subscription-footer');
        if (footer && typeof /** @type {any} */ (footer).syncAddButtonsForStepQuota === 'function') {
          void /** @type {any} */ (footer).syncAddButtonsForStepQuota(
            parsed.items ? { items: parsed.items } : undefined
          );
        }
      }

      sectionsToUpdate.forEach((sectionId) => {
        if (parsed.sections?.[sectionId]) {
          try {
            morphSection(sectionId, parsed.sections[sectionId]);
          } catch (e) {
            console.warn('[AddToCartComponent] morphSection:', e);
          }
        }
      });

      finishMinus(true);
    } catch (err) {
      console.error('[AddToCartComponent] decreaseQuantity:', err);
      finishMinus(false);
    }
  }

  /**
   * Build section IDs to request for cart change (mirrors component-cart-items).
   * @returns {Set<string>}
   */
  #buildSectionsForCartChange() {
    const sectionsToUpdate = new Set();
    const cartItems = document.querySelector('cart-items-component');
    const sectionId = cartItems?.getAttribute('data-section-id') || (cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined);
    if (sectionId) sectionsToUpdate.add(sectionId);

    const modalId = document.querySelector('[data-tv-subscription-cart-modal][data-section-id]')?.getAttribute('data-section-id');
    if (modalId) sectionsToUpdate.add(modalId);

    const mainContent = document.getElementById('MainContent');
    if (mainContent) {
      const footer = mainContent.querySelector('custom-collection-footer-component');
      const footerId = footer?.getAttribute('data-section-id');
      if (footerId) sectionsToUpdate.add(footerId);
    }

    const mealSection = document.querySelector('.meal-delivery-moment-app-section');
    const mealId = mealSection?.getAttribute('data-section-id');
    if (mealId) sectionsToUpdate.add(mealId);

    const deliverySection = document.querySelector('delivery-moment-component')?.closest('[id^="shopify-section-"]');
    if (deliverySection) {
      const id = deliverySection.id?.replace('shopify-section-', '');
      if (id) sectionsToUpdate.add(id);
    }

    document.querySelectorAll('cart-items-component').forEach((el) => {
      const id = el.getAttribute('data-section-id') || (el instanceof HTMLElement ? el.dataset.sectionId : undefined);
      if (id) sectionsToUpdate.add(id);
    });

    return sectionsToUpdate;
  }

  /**
   * Animates the minus button with checkmark (no fly animation).
   * @param {HTMLButtonElement} minusButton - The minus button element.
   */
  #animateMinusButton(minusButton) {
    if (!minusButton) return;
    if (this.#minusAnimationTimeout) clearTimeout(this.#minusAnimationTimeout);
    if (this.#minusCleanupTimeout) clearTimeout(this.#minusCleanupTimeout);

    minusButton.classList.add('atc-added');
    this.#minusAnimationTimeout = setTimeout(() => {
      this.#minusCleanupTimeout = setTimeout(() => {
        minusButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  async handleClick(event) {
    logMessage('log', 'Add to cart button clicked');

    // Prevent form submission since we handle it in the button click
    event.preventDefault();

    const form = this.closest('form');
    if (!form) {
      logMessage('error', 'No form found');
      return;
    }

    // Check if this form has already been validated by ProductFormComponent
    if (form.dataset.deliveryValidated === 'true') {
      logMessage('log', 'Form already validated, proceeding with submission');
      // Trigger the form submission manually
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(submitEvent);
      return;
    }

    const shouldProceed = await handleProductSubmission(form, event);
    if (!shouldProceed) {
      return;
    }

    logMessage('log', 'Proceeding with normal button click flow');

    // Mark the form as validated to prevent duplicate checks
    form.dataset.deliveryValidated = 'true';

    // Trigger the form submission manually
    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    form.dispatchEvent(submitEvent);

    // Only trigger animations if the form submission was successful
    // The animations will be triggered by the form submission success
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   * Skips when the cart icon is hidden (e.g. on subscription template - cart button has no-disp).
   */
  animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;
    // Cart is hidden on subscription template; skip fly animation
    if (cartIcon.closest('.no-disp')) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    const captureDeliveryMoment = this.dataset.captureDeliveryMoment;

    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Reset delivery validation flag for new submissions
    const productForm = this.querySelector('form');
    if (productForm) {
      delete productForm.dataset.deliveryValidated;
    }

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Show loading state
    this.refs.addToCartButtonContainer?.showLoading();


    // Check postal code and delivery time before proceeding with form submission
    if (!productForm) {
      logMessage('error', 'No form found');
      this.refs.addToCartButtonContainer?.hideLoading();
      return;
    }

    if (captureDeliveryMoment === 'true') {
      // Check if delivery validation has already been done by AddToCartComponent
      if (productForm.dataset.deliveryValidated === 'true') {
        logMessage('log', 'Delivery already validated by AddToCartComponent, skipping check');
        // Clear the flag for future submissions
        delete productForm.dataset.deliveryValidated;
      } else {
        const shouldProceed = await handleProductSubmission(productForm, event);
        if (!shouldProceed) {
          this.refs.addToCartButtonContainer?.hideLoading();
          return;
        }
      }
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);


    // Add subscription/person metadata to line item properties.
    // - Oneoff: assign _profile_key = "oneoff" so selling plans are not applied at checkout.
    // - Soup/Dessert: assign _profile_key to step handle (soup/dessert) when product type matches.
    // - Person step (p1..pN): send person profile key + display name.
    const toggleOptionsElement = document.querySelector('[data-ecomm-person-toggle-options]');
    const ACTIVE_STEP_KEY = 'tv_subscription_active_step_handle';

    /** @returns {string} */
    const getActiveStepHandle = () => {
      try {
        return String(sessionStorage.getItem(ACTIVE_STEP_KEY) || '').trim() || 'p1';
      } catch {
        return 'p1';
      }
    };

    /** @param {string} handle */
    const isPersonStep = (handle) => /^p\d+$/i.test(String(handle || ''));

    /** @param {any} s */
    const norm = (s) => String(s || '').trim().toLowerCase();

    // Detect the product's Shopify type from the product grid item (set in `sections/main-collection.liquid`).
    const productTypeCarrier =
      this.closest('[data-ecomm-shopify-type]') ||
      form.closest('[data-ecomm-shopify-type]') ||
      form.closest('product-card')?.closest('[data-ecomm-shopify-type]');
    const shopifyType = productTypeCarrier instanceof Element ? productTypeCarrier.getAttribute('data-ecomm-shopify-type') : '';
    const shopifyTypeNorm = norm(shopifyType);


    /** @type {string | null} */
    let matchedStepHandle = null;

    if (toggleOptionsElement instanceof HTMLElement && shopifyTypeNorm) {
      // Build a map from translated step value (data-value, e.g. "Soep"/"Nagerechten") → step handle ("soup"/"dessert")
      const stepInputs = Array.from(toggleOptionsElement.querySelectorAll('input[name="ecomm-step"]')).filter(
        (el) => el instanceof HTMLInputElement
      );

      const handleByValue = new Map();
      stepInputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const handle = norm(input.value);
        const dataValue = norm(input.dataset?.value ?? input.getAttribute('data-value'));
        if (handle && dataValue) {
          handleByValue.set(dataValue, handle);
        }
      });

      // 1) Prefer translation-driven matching (no hardcoded Dutch strings).
      const fromDataValue = handleByValue.get(shopifyTypeNorm);
      if (fromDataValue) {
        matchedStepHandle = fromDataValue;
      } else {
        // 2) Fallback heuristics (in case translations don't match Shopify product types 1:1).
        if (shopifyTypeNorm === 'soep' || shopifyTypeNorm === 'soup' || shopifyTypeNorm.includes('soep')) {
          matchedStepHandle = 'soup';
        } else if (shopifyTypeNorm === 'nagerechten' || shopifyTypeNorm === 'toetjes' || shopifyTypeNorm === 'dessert' || shopifyTypeNorm.includes('nagerechten') || shopifyTypeNorm.includes('toetjes')) {
          matchedStepHandle = 'dessert';
        }
      }
    }

    const activeStepHandle = getActiveStepHandle();
    const toggleRoot = toggleOptionsElement?.closest('[data-ecomm-person-toggle]');
    const mode = (toggleRoot instanceof HTMLElement ? String(toggleRoot.dataset?.ecommToggleMode || '').trim().toLowerCase() : '') || 'ecomm';
    const isEcomm = mode === 'ecomm';
    const isSubscription = mode === 'subscription';

    /** Stamp lines only when adding from the subscription collection in subscription flow (not ecomm / other templates). */
    const shouldStampSubscriptionLineUid = isSubscriptionCollectionTemplate() && isSubscription;

    const personTogglerVisible = document.querySelector('[data-ecomm-person-toggle]:not(.no-disp)') != null;
    const checkedProfileInput = toggleOptionsElement?.querySelector('input[name="ecomm-profile"]:checked');
    const checkedProfileKey = checkedProfileInput instanceof HTMLInputElement ? String(checkedProfileInput.value || '').trim() : '';

    /** @param {string} key @param {string|null|undefined} displayName */
    const appendProfileKey = (key, displayName) => {
      formData.append('properties[_profile_key]', key);
      if (displayName) formData.append('properties[_Profiel naam]', displayName);
      logMessage('log', 'Assigned _profile_key:', key);
    };

    // Look up the human-readable label (data-value) for a step handle from the toggle inputs.
    // Used to send a hidden profile label for soup, dessert, and oneoff items.
    // Shopify hides properties that start with _ in checkout, but the theme can still read them.
    /** @param {string} handle @returns {string | null} */
    const getStepLabel = (handle) => {
      const input = Array.from(toggleOptionsElement?.querySelectorAll('input[name="ecomm-step"]') ?? [])
        .find((el) => el instanceof HTMLInputElement && norm(/** @type {HTMLInputElement} */ (el).value) === norm(handle));
      return input instanceof HTMLInputElement ? ((input.getAttribute('data-value') || '').trim() || null) : null;
    };

    if (isEcomm) {
      // ECOMM: person toggler, no steps. Soup/dessert by product type; meals by person or oneoff.
      if (matchedStepHandle) {
        appendProfileKey(matchedStepHandle, getStepLabel(matchedStepHandle));
      } else if (personTogglerVisible && checkedProfileKey) {
        const label = toggleOptionsElement?.querySelector(`label[for="${checkedProfileInput?.id}"]`);
        const displayName = label instanceof HTMLElement ? label.textContent?.trim() : checkedProfileKey;
        appendProfileKey(checkedProfileKey, displayName || undefined);
      } else {
        appendProfileKey('oneoff', getStepLabel('oneoff'));
      }
    } else if (isSubscription) {
      // SUBSCRIPTION: steps. Active step drives _profile_key.
      const step = norm(activeStepHandle);
      if (step === 'oneoff') {
        appendProfileKey('oneoff', getStepLabel('oneoff'));
      } else if (step === 'soup' || step === 'dessert') {
        appendProfileKey(step, getStepLabel(step));
      } else if (isPersonStep(activeStepHandle)) {
        const label = toggleOptionsElement?.querySelector(`label[for="${checkedProfileInput?.id}"]`);
        const displayName = label instanceof HTMLElement ? label.textContent?.trim() : activeStepHandle;
        appendProfileKey(activeStepHandle, displayName || undefined);
      } else {
        appendProfileKey('oneoff', getStepLabel('oneoff'));
      }
    } else {
      appendProfileKey('oneoff', getStepLabel('oneoff'));
    }

    // Add selling plan at add time when on subscription collection template (not oneoff)
    // Only add if the variant supports this selling plan (avoids "Cannot apply selling plan to variant" error)
    /** @type {string | null} */
    let appliedSellingPlanId = null;
    if (isSubscriptionCollectionTemplate() && isSubscription && norm(activeStepHandle) !== 'oneoff') {
      const surveyPlanId = getSurveySellingPlanId();
      if (surveyPlanId) {
        const variantPlanIds = String(this.dataset.variantSellingPlanIds || '').trim();
        const supportedIds = variantPlanIds ? variantPlanIds.split(',').map((s) => s.trim()).filter(Boolean) : [];
        const variantSupportsPlan = supportedIds.includes(String(surveyPlanId));
        if (variantSupportsPlan) {
          appliedSellingPlanId = String(surveyPlanId);
          formData.append('selling_plan', appliedSellingPlanId);
          logMessage('log', 'Added selling_plan at add time:', surveyPlanId);
        }
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];

    const subscriptionModalSectionId = document.querySelector('[data-tv-subscription-cart-modal][data-section-id]')?.getAttribute('data-section-id');
    if (subscriptionModalSectionId) {
      cartItemComponentsSectionIds.push(subscriptionModalSectionId);
    }

    const mainContent = document.getElementById('MainContent');
    if (mainContent) {
      const sectionsWithCollectionFooter = mainContent.querySelectorAll('[id^="shopify-section-"]:has(custom-collection-footer-component)');
      sectionsWithCollectionFooter.forEach(section => {
        const sectionId = section.id.replace('shopify-section-', '');
        cartItemComponentsSectionIds.push(sectionId);
        logMessage('log', 'Adding section containing collection footer:', sectionId);
      });

      // Do NOT add collection-subscription-footer: it updates via JS on cart update.
      // Morphing it causes flicker; we update cart count, total, and button states client-side.

      // Add delivery moment section if cart is empty (first item being added)
      if (cartItemsComponents.length == 1) {
        const deliveryMomentSection = document.querySelector('delivery-moment-component')?.closest('[id^="shopify-section-"]');
        if (deliveryMomentSection) {
          const sectionId = deliveryMomentSection.id.replace('shopify-section-', '');
          cartItemComponentsSectionIds.push(sectionId);
          logMessage('log', 'Adding delivery moment section due to empty cart:', sectionId);
        }
      }
    }

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const quantityRaw = formData.get('quantity');
    const lineQty = Math.max(1, parseInt(String(quantityRaw ?? '1'), 10) || 1);

    /** @type {Promise<any>} */
    let cartAddPromise;
    if (lineQty > 1) {
      const variantId = formData.get('id');
      const vid = variantId != null ? Number(variantId) : NaN;
      if (!Number.isFinite(vid)) {
        logMessage('error', 'Variant id missing for cart line split');
        this.refs.addToCartButtonContainer?.hideLoading();
        return;
      }
      const baseProps = collectCartLinePropertiesFromFormData(formData);
      const items = Array.from({ length: lineQty }, () => {
        /** @type {Record<string, unknown>} */
        const line = {
          id: vid,
          quantity: 1,
          properties: {
            ...baseProps,
            ...(shouldStampSubscriptionLineUid
              ? { _subscription_line_uid: newSubscriptionLineUid() }
              : {}),
          },
        };
        if (appliedSellingPlanId) {
          line.selling_plan = Number(appliedSellingPlanId);
        }
        return line;
      });
      const body = /** @type {Record<string, unknown>} */ ({ items });
      if (cartItemComponentsSectionIds.length > 0) {
        body.sections = cartItemComponentsSectionIds.join(',');
      }
      cartAddPromise = fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      }).then((r) => r.json());
    } else {
      if (shouldStampSubscriptionLineUid) {
        formData.append('properties[_subscription_line_uid]', newSubscriptionLineUid());
      }
      const fetchCfg = fetchConfig('javascript', { body: formData });
      cartAddPromise = fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      }).then((response) => response.json());
    }

    cartAddPromise
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: lineQty || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          // Clear delivery validation flag on error to allow retry
          if (productForm) {
            delete productForm.dataset.deliveryValidated;
          }

          // Hide loading state on error
          this.refs.addToCartButtonContainer?.hideLoading();

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          const quickAddDialogEl = document.getElementById('custom-quick-add-dialog');
          const fromQuickAddModal = !!(quickAddDialogEl && quickAddDialogEl.contains(this));

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), /** @type {any} */ ({
              source: 'product-form-component',
              itemCount: lineQty || Number(this.dataset.quantityDefault),
              productId: response.product_id,
              sections: response.sections,
              fromQuickAddModal,
            }))
          );

          // Trigger animations only on successful cart addition
          if (this.refs.addToCartButtonContainer) {
            this.refs.addToCartButtonContainer.animateAddToCart();
            const target = /** @type {HTMLElement} */ (event.target);
            if (!target?.closest('.quick-add-modal')) {
              this.refs.addToCartButtonContainer.animateFlyToCart();
            }
            // Hide loading state on success
            this.refs.addToCartButtonContainer.hideLoading();
          }

          logMessage('log', 'Cart add response:', response);
          const newSections = response.sections;
          Object.entries(newSections).forEach(([sectionId, sectionHtml]) => {
            if (sectionId && sectionHtml) {
              try {
                morphSection(sectionId, sectionHtml);
              } catch (error) {
                logMessage('warn', `Failed to morph section ${sectionId}:`, error);
              }
            }
            else {
              logMessage('warn', `Section ${sectionId} not found in response`);
            }
          });

          const productUrl = response.url;

          // Product card overlay/badges updated via CartAddEvent listener (product-card-updater)

          // Also update the custom quick add dialog if present/open with fresh product page HTML
          try {
            const quickAddDialog = /** @type {HTMLElement | null} */ (document.getElementById('custom-quick-add-dialog'));
            if (quickAddDialog && productUrl) {
              Promise.all([
                fetch(productUrl).then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`)))),
                DeliveryCartAttributes.getCartData(),
              ])
                .then(([htmlText, cartData]) => {
                  const parsed = new DOMParser().parseFromString(htmlText, 'text/html');
                  const productGrid = parsed.querySelector('[data-product-grid-content]');
                  const customBuyButtons = productGrid ? productGrid.querySelector('.custom-buy-buttons') : null;
                  const modalFooter = document.getElementById('custom-quick-add-modal__footer');

                  if (customBuyButtons && modalFooter) {
                    morph(modalFooter, customBuyButtons);
                  }

                  const variantSelectDropdownElement = /** @type {VariantSelectDropdown | null} */ (modalFooter?.querySelector('variant-select-dropdown'));
                  const variantSelectDropdown = modalFooter?.querySelector('variant-select-dropdown');
                  if (variantSelectDropdownElement && variantSelectDropdown) {
                    const variantToSelect = variantSelectDropdown.querySelector(`.dropdown-menu-item[data-variant-id="${response.variant_id}"]`);
                    const variantIndex = variantToSelect ? variantToSelect.getAttribute("data-index") || '0' : '0';
                    if (variantSelectDropdownElement && variantIndex) {
                      variantSelectDropdownElement.selectItem(parseInt(variantIndex));
                    }
                  }

                  void syncQuickAddModalFooterState(cartData);
                  if (fromQuickAddModal) {
                    const footer = document.querySelector('custom-collection-subscription-footer');
                    if (footer && typeof /** @type {any} */ (footer).syncAddButtonsForStepQuota === 'function') {
                      void /** @type {any} */ (footer).syncAddButtonsForStepQuota(cartData);
                    }
                  }
                })
                .catch((err) => {
                  console.log('[QUICK-ADD] Failed to update custom quick add dialog', err);
                });
            }
          } catch (e) {
            console.log('[QUICK-ADD] unexpected error during dialog update', e);
          }
        }
      })
      .catch((error) => {
        logMessage('error', 'Cart add error:', error);
        // Clear delivery validation flag on error to allow retry
        if (productForm) {
          delete productForm.dataset.deliveryValidated;
        }
        // Hide loading state on error
        this.refs.addToCartButtonContainer?.hideLoading();
      })
      .finally(() => {
        // Clear delivery validation flag after operation completes
        if (productForm) {
          delete productForm.dataset.deliveryValidated;
        }
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

// Make functions globally available
/** @type {any} */ (window).handleProductSubmission = handleProductSubmission;
